// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    getSelectedCategoryPath,
    getSelectedProductPath,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import mixpanel from "mixpanel-browser";
import { css } from "styled-components";

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    mixPanelApiKey = "mixPanelApiKey",
}

const mapStateToProps = (state: any) => {
    const currentPage = getCurrentPage(state);
    const location = getLocation(state);

    return {
        currentPage,
        location,
        state,
    };
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<any> & {
        fields: {
            [fields.mixPanelApiKey]: string;
        };
    };

interface AnalyticsStyles {
    container?: InjectableCss;
}

const analyticsStyles: AnalyticsStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = analyticsStyles;
const Analytics: React.FC<Props> = ({ currentPage, location, state, fields: { mixPanelApiKey }, dispatch }) => {
    const { isInShell } = useShellContext();
    const customerType = getSession(state).properties?.["mpExistingCustomer"];
    React.useEffect(() => {
        mixpanel.init(mixPanelApiKey);
        (window as any).mixpanelInitialized = true;
    }, []);

    React.useEffect(() => {
        if (currentPage.type === "ProductListPage") {
            const categoryPath =
                getSelectedCategoryPath(state) ||
                (location?.pathname?.toLowerCase().startsWith("/content/") ? "" : location.pathname);
            const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
            mixpanel.track("Product Listing Pageview", {
                "Category Name": catalogPageState.value?.title,
                customer_type: customerType || "new",
            });
        } else if (currentPage.type === "ProductDetailsPage") {
            const location = getLocation(state);
            const productPath =
                getSelectedProductPath(state) ||
                (location?.pathname?.toLowerCase().startsWith("/content/") ? "" : location.pathname);
            const productState = getProductStateByPath(state, productPath)?.value;
            mixpanel.track("Product Detail Pageview", {
                "Product Name": productState?.productTitle,
                customer_type: customerType || "new",
            });
        } else if (currentPage.type === "CartPage") {
            mixpanel.track("Cart Pageview", { customer_type: customerType || "new" });
        } else {
            mixpanel.track("Default Pageview", {
                "Page Name": window.document.title,
                customer_type: customerType || "new",
            });
        }
    }, [currentPage]);

    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="SearchEngineOptimization">
                {translate("Analytics Widget")} v.2021.0.0
            </StyledWrapper>
        );
    }

    return <></>;
};

const definition: WidgetDefinition = {
    group: "Common",
    displayName: "Analytics Widget",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.mixPanelApiKey,
            displayName: "Mixpanel Api-Key",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Analytics),
    definition,
};

export default widgetModule;
